/*
 * Upsell Modal
 * ------------
 * Works with sections/upsell-modal.liquid.
 *
 * Flow (trigger = Add to Cart):
 *   1. Shopper adds a product that matches an upsell rule (rule matched
 *      server-side, so the modal for the current product is already rendered).
 *   2. We intercept that product's add-to-cart submit, add it ourselves (using
 *      the form's own data so properties / selling plans are preserved), and
 *      refresh the cart drawer the same way the theme does (cartUpdate pubsub
 *      with freshly rendered sections).
 *   3. Instead of opening the cart drawer, we open the upsell modal.
 *   4. Each "Add" in the modal adds that product via /cart/add.js with a hidden
 *      line item property `_upsell_N: true` (N = the rule/block order), then
 *      closes the modal and opens the cart drawer.
 *   5. Dismissing the modal any way — "Continue to cart", the X button, a
 *      backdrop click, or Escape — closes it and opens the cart drawer, since
 *      the trigger product is already in the cart.
 *
 * All cart requests use the .js Ajax endpoints, which always return JSON.
 */
(function () {
  'use strict';

  var FT = window.FoxTheme || {};

  function routeRoot() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    return root;
  }
  function addUrl() { return routeRoot() + 'cart/add.js'; }
  function cartUrl() { return routeRoot() + 'cart.js'; }

  /* ---- fetch helpers --------------------------------------------------- */

  function postJSON(url, data) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data)
    }).then(parseJSON);
  }

  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } }).then(parseJSON);
  }

  // Parse JSON, but throw a clear error if the server returned HTML (e.g. a
  // redirect or error page) so we never silently choke on "<!DOCTYPE".
  function parseJSON(response) {
    return response.text().then(function (text) {
      try {
        return JSON.parse(text);
      } catch (e) {
        throw new Error('Expected JSON from ' + response.url + ' but got: ' + text.slice(0, 60));
      }
    });
  }

  // Section ids the cart drawer (and friends) want re-rendered after a change.
  function getBundledSections() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  function publishCartUpdate(cart) {
    if (FT.pubsub && FT.pubsub.publish && FT.pubsub.PUB_SUB_EVENTS) {
      FT.pubsub.publish(FT.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
    }
    // Generic fallbacks for any non-FoxTheme listeners.
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
  }

  // Add one item and refresh the cart UI the way the theme does.
  // `item` is a /cart/add.js single-item payload (id, quantity, properties...).
  function addItemAndRefresh(item) {
    var payload = Object.assign({}, item, {
      sections: getBundledSections(),
      sections_url: window.location.pathname
    });
    return postJSON(addUrl(), payload).then(function (added) {
      if (added && added.status) {
        // Shopify error object (e.g. sold out).
        var err = new Error(added.description || added.message || 'Add to cart failed');
        err.cartError = added;
        throw err;
      }
      return getJSON(cartUrl()).then(function (cart) {
        if (added && added.sections) cart.sections = added.sections;
        // onCartUpdate needs cart.sections; only publish when present.
        if (cart.sections) {
          publishCartUpdate(cart);
        } else {
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
        }
        return cart;
      });
    });
  }

  function getCartDrawer() { return document.querySelector('cart-drawer'); }

  function openCartDrawer(opener) {
    var drawer = getCartDrawer();
    if (drawer && typeof drawer.show === 'function') {
      drawer.show(opener || null);
    } else {
      var btn = document.querySelector('.cart-drawer-button');
      if (btn) btn.click();
    }
  }

  /* ---- modal registry -------------------------------------------------- */

  var byVariant = {};
  var byProduct = {};

  function indexModals() {
    byVariant = {};
    byProduct = {};
    document.querySelectorAll('.upsell-modal[data-upsell-index]').forEach(function (modal) {
      var pid = modal.getAttribute('data-trigger-product-id');
      if (pid) byProduct[String(pid)] = modal;
      (modal.getAttribute('data-trigger-variant-ids') || '').split(',').forEach(function (v) {
        v = (v || '').trim();
        if (v) byVariant[v] = modal;
      });
    });
  }

  function findModalForForm(form) {
    var info = form.closest('product-info');
    var pid = info && info.getAttribute('data-product-id');
    if (pid && byProduct[String(pid)]) return byProduct[String(pid)];

    var idInput = form.querySelector('[name="id"]');
    if (idInput && byVariant[String(idInput.value)]) return byVariant[String(idInput.value)];

    return null;
  }

  /* ---- open / close ---------------------------------------------------- */

  var lastTrigger = null;

  function openModal(modal, opener) {
    lastTrigger = opener || null;
    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');
    void modal.offsetWidth;
    modal.classList.add('is-open');
    document.documentElement.classList.add('upsell-modal-open');
    document.body.style.overflow = 'hidden';
    var close = modal.querySelector('.upsell-modal__close');
    if (close) close.focus();
  }

  function closeModal(modal, opts) {
    if (!modal) return;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('hidden', '');
    document.documentElement.classList.remove('upsell-modal-open');
    document.body.style.overflow = '';
    if (opts && opts.openCart) openCartDrawer(lastTrigger);
    lastTrigger = null;
  }

  function getOpenModal() { return document.querySelector('.upsell-modal.is-open'); }

  /* ---- trigger product (intercept Add to Cart) ------------------------- */

  function formToItem(form) {
    var fd = new FormData(form);
    var item = { id: fd.get('id'), quantity: Number(fd.get('quantity')) || 1 };
    var props = {};
    fd.forEach(function (val, key) {
      if (key.indexOf('properties[') === 0 && key.charAt(key.length - 1) === ']') {
        props[key.slice(11, -1)] = val;
      }
    });
    if (Object.keys(props).length) item.properties = props;
    var sp = fd.get('selling_plan');
    if (sp) item.selling_plan = sp;
    return item;
  }

  function setSubmitLoading(form, loading) {
    var btn = form.querySelector('[type="submit"], [name="add"]');
    if (!btn) return;
    if (loading) {
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('btn--loading');
    } else {
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('btn--loading');
    }
  }

  function handleProductFormSubmit(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;

    var isProductForm =
      form.getAttribute('is') === 'product-form' ||
      (form.getAttribute('action') || '').indexOf('/cart/add') !== -1 ||
      !!form.querySelector('[name="add"]');
    if (!isProductForm) return;

    if (document.body.classList.contains('cart-template')) return;

    if (form.dataset.upsellBypass === '1') {
      delete form.dataset.upsellBypass;
      return; // let the theme handle it normally
    }

    var modal = findModalForForm(form);
    if (!modal) return; // no rule for this product

    // Take over so the theme's own submit handler does not also run.
    event.preventDefault();
    event.stopImmediatePropagation();

    setSubmitLoading(form, true);

    addItemAndRefresh(formToItem(form))
      .then(function () {
        openModal(modal, event.submitter || form.querySelector('[type="submit"]'));
      })
      .catch(function (err) {
        console.error('[upsell-modal] trigger add failed', err);
        // Fall back to the theme's native flow so the shopper is never blocked.
        form.dataset.upsellBypass = '1';
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(event.submitter || undefined);
        } else {
          form.submit();
        }
      })
      .finally(function () {
        setSubmitLoading(form, false);
      });
  }

  /* ---- upsell products (modal Add buttons) ----------------------------- */

  function addUpsellProduct(btn) {
    var variantId = btn.getAttribute('data-variant-id');
    var index = btn.getAttribute('data-upsell-index');
    if (!variantId || !index) return;

    var properties = {};
    properties['_upsell_' + index] = 'true';

    btn.classList.add('is-loading');
    btn.disabled = true;

    addItemAndRefresh({ id: Number(variantId), quantity: 1, properties: properties })
      .then(function () {
        // Per request: after adding an upsell, close the modal and open the cart.
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        closeModal(btn.closest('.upsell-modal'), { openCart: true });
      })
      .catch(function (err) {
        console.error('[upsell-modal] upsell add failed', err);
        btn.classList.remove('is-loading');
        btn.disabled = false;
        var textEl = btn.querySelector('.upsell-card__add-text');
        if (textEl) textEl.textContent = 'Try again';
      });
  }

  /* ---- wiring ---------------------------------------------------------- */

  // Capture phase so we run before the theme's ProductForm submit listener.
  document.addEventListener('submit', handleProductFormSubmit, true);

  document.addEventListener('click', function (event) {
    var addBtn = event.target.closest('[data-upsell-add]');
    if (addBtn) {
      event.preventDefault();
      if (addBtn.classList.contains('is-added') || addBtn.disabled) return;
      addUpsellProduct(addBtn);
      return;
    }

    var continueBtn = event.target.closest('[data-upsell-continue]');
    if (continueBtn) {
      event.preventDefault();
      closeModal(continueBtn.closest('.upsell-modal'), { openCart: true });
      return;
    }

    var closer = event.target.closest('[data-upsell-close]');
    if (closer) {
      event.preventDefault();
      // The trigger product is already in the cart by this point, so dismissing
      // the modal in any way (X button or backdrop) takes the shopper to the cart.
      closeModal(closer.closest('.upsell-modal'), { openCart: true });
      return;
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      var open = getOpenModal();
      if (open) closeModal(open, { openCart: true });
    }
  });

  document.addEventListener('shopify:section:load', indexModals);
  document.addEventListener('shopify:section:unload', indexModals);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', indexModals, { once: true });
  } else {
    indexModals();
  }
})();
